/**
 * cl-core/money — 만원 금액 표기 «정본» 구현(2026-10-24 HC 정리). 순수 TS · 형제 import 0.
 *
 *   짧은 표기  eokShort / eokShortNum — 카드·칩·표 머리·OG·알림. 소수 첫째 자리 «버림» · 「.0」 제거.
 *     69,500 → 「6.9억」 · 99,600 → 「9.9억」 · 100,000 → 「10억」 · 90,500 → 「9억」 · 125,000 → 「12.5억」 · 9,800 → 「9,800만」.
 *     ⛔ 반올림(toFixed) 금지 — 분양가 하한을 올려 「X억부터」 가 실제보다 비싼 바닥을 말한다(69,500 → 「7.0억~」).
 *   정확 표기  eokManLabel / eokManWon — 표·본문·사실 문장. 「6억 9,500만」·「6억 9,500만원」·「12억(원)」.
 *
 * 공개 통로: 짧은 표기는 `@/lib/fmt`, 정확 표기는 `@/lib/format/money` 가 다시 내보낸다(이 파일을 직접 부르는 건 cl-core 안쪽뿐).
 * ⚠️ cl-core 는 Deno 에서도 읽힌다 — 외부 패키지 · Node API · '@/' 별칭 import 금지.
 */

const posNum = (v: unknown): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'number' ? v : Number(String(v).replace(/,/g, ''));
  return Number.isFinite(n) && n > 0 ? n : null;
};

/** 만원 → 억 숫자부 「6.9」·「10」(버림 · 「.0」 제거). 0·음수·결측 → null. 문턱(1억 미만 처리)은 호출부. */
export function eokShortNum(manwon: unknown): string | null {
  const n = posNum(manwon);
  if (n === null) return null;
  const tenths = Math.floor(Math.round(n) / 1000); // 만원 정수로 맞춘 뒤 0.1억(=1,000만) 단위 버림
  const e = Math.floor(tenths / 10);
  const d = tenths % 10;
  return d ? `${e}.${d}` : String(e);
}

/** 만원 → 「6.9억」·「9,800만」(suffix '원' → 「6.9억원」·「9,800만원」). 0·음수·결측 → null. */
export function eokShort(manwon: unknown, suffix: '' | '원' = ''): string | null {
  const n = posNum(manwon);
  if (n === null) return null;
  const v = Math.round(n);
  if (v < 10000) return `${v.toLocaleString('ko-KR')}만${suffix}`;
  return `${eokShortNum(v)}억${suffix}`;
}

/**
 * 만원 → 「N억 M만」 정확 표기. 0·비정상 → null. 반올림 없음(정수 만원).
 *   suffix '원' → 만 꼬리 뒤에 「원」(「9억 20만원」·「9,800만원」). wonOnEok → 억이 딱 떨어질 때도 「12억원」(문장용).
 */
export function eokManLabel(manwon: unknown, suffix: '' | '원' = '', wonOnEok = false): string | null {
  const v = Math.round(Number(manwon));
  if (!Number.isFinite(v) || v <= 0) return null;
  const eok = Math.floor(v / 10000);
  const man = v % 10000;
  if (eok <= 0) return `${man.toLocaleString('ko-KR')}만${suffix}`;
  return man === 0 ? `${eok}억${wonOnEok ? suffix : ''}` : `${eok}억 ${man.toLocaleString('ko-KR')}만${suffix}`;
}

/** 문장용 정확 표기 — 「3억 6,900만원」·「5억원」·「8,500만원」. 0·비정상 → null. */
export const eokManWon = (manwon: unknown): string | null => eokManLabel(manwon, '원', true);
